import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useAppState, useBrandId } from 'AppProvider';
import useCustomHistory from 'hooks/use-custom-history';
import { ANALYTICS_EVENT, ANALYTICS_PROPERTY } from '@kouto/types';
import { usePageViewEvent } from 'hooks/usePageViewEvent';
import { analyticsManager } from 'features/analytics';
import { useRecommendationsViewEvent } from 'features/analytics/hooks/useRecommendationsViewEvent';
import {
  FEATURED_FALLBACK_LIST_NAME,
  RECOMMENDATIONS_LIST_NAME,
} from 'features/analytics/utils/recommendationAnalytics';
import { Check } from 'components/ExperiencesCalendar/Filters/CustomComponents/Check';
import { Divider } from 'components/common/styled/common-styled';
import useFeaturedListingsV3 from 'api/listing/useFeaturedListingsV3/useFeaturedListingsV3';
import useSupportedLanguage from 'utils/data/language/useSupportedLanguage';
import { BookingConfirmationCard } from 'components/molecules/BookingConfirmationCard';
import { BookingConfirmationCardSkeleton } from 'components/molecules/BookingConfirmationCard/BookingConfirmationCardSkeleton';
import { RecommendationCardGrid } from 'components/molecules/RecommendationCardGrid';
import { FeaturedListingV3 } from 'types/featuredListingV3';
import { PageTitle } from 'components/atoms/PageTitle';
import { ReducerState } from 'types/reducer';
import { useIsMobile } from 'WindowDimensionProvider';
import { useSearchParamsObject } from 'utils/url';
import useGetListingRecommendations from 'api/listingRecommendation/useGetListingRecommendations';
import { Button } from 'components/theme/Button';
import { useTranslation } from 'react-i18next';
import useListingsV3 from 'api/listing/useListingsV3/useListingsV3';
import { GetListingRecommendationsResponse } from 'api/listingRecommendation/getListingRecommendations';

const TopSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 72px 24px 0 24px;
  font-family: ${({ theme }) => theme.font.primaryFont};
  gap: 12px;
`;

const Heading = styled.h2`
  font-weight: 700;
  font-family: inherit;
  font-size: 32px;
  color: var(--way-colors-primaryTextColor);
  margin: 0;
`;

const Text = styled.p`
  font-weight: 400;
  letter-spacing: 0.02px;
  color: var(--Text-Tertiary, rgba(0, 0, 0, 0.6));
  font-family: inherit;
  margin: 0 !important;
`;

const Title = styled.h3`
  font-weight: 700;
  line-height: 30.8px;
  font-family: inherit;
  font-size: 28px;
  margin: 0;
  color: var(--way-colors-primaryTextColor);
`;

const IconWrapper = styled.div`
  border-radius: 50%;
  border: 2px solid var(--Text-Tertiary, rgba(0, 0, 0, 0.6));
`;

const BottomSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  font-family: ${({ theme }) => theme.font.primaryFont};
  gap: 30px;
  padding: 0 24px 24px 24px;
`;

const TextSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const LinkBackButton = styled(Button)`
  border: none;
  outline: none;
  margin-top: 30px;
  display: inline-block;
`;

const BookingConfirmation = () => {
  const { t: translate } = useTranslation();
  const { status, lang } = useSearchParamsObject<{
    status?: string;
    lang?: string;
  }>();
  const { push: customPush, replace: customReplace } = useCustomHistory();
  const emailAddress = useRef(
    window.sessionStorage.getItem('confirmation_email'),
  );
  const recommendationIdRef = useRef(
    window.sessionStorage.getItem('recommendation_id'),
  );
  const recommendationDateRangeRef = useRef(
    window.sessionStorage.getItem('recommendation_date_range'),
  );
  const brandId = useBrandId();
  const { response } = useAppState((state: ReducerState) => state.booking);
  const languageCode = useSupportedLanguage();
  const isMobile = useIsMobile();

  const { listings } = useListingsV3({
    limit: 3,
    language: languageCode,
    isUnlisted: false,
  });

  const { data: featuredListings, isLoading: isFeaturedListingsLoading } =
    useFeaturedListingsV3({
      language: languageCode,
    });

  const {
    listings: recommendedListings,
    isCompleted,
    isFailed,
    isPolling,
  } = useGetListingRecommendations({
    recommendationId: recommendationIdRef.current,
    maxPolls: 15,
    pollInterval: 2000,
    language: languageCode,
  });

  const hasRecommendations = isCompleted && recommendedListings.length > 0;
  const hasFeaturedListings = !!(
    featuredListings &&
    !isFeaturedListingsLoading &&
    featuredListings.length > 0
  );
  const shouldShowFeaturedListings =
    !hasRecommendations && hasFeaturedListings && (isFailed || isCompleted);
  const shouldShowRegularListings =
    !hasRecommendations && !hasFeaturedListings && (isFailed || isCompleted);

  const getDisplayListings = (
    hasRecommendations: boolean,
    shouldShowFeaturedListings: boolean,
    shouldShowRegularListings: boolean,
  ): FeaturedListingV3[] => {
    if (hasRecommendations) {
      return recommendedListings.map(
        (listing: GetListingRecommendationsResponse['listings'][number]) => ({
          ...listing,
          isUnlisted: listing.isUnlisted ?? false,
          category: listing.category
            ? {
                ...listing.category,
                brandId: brandId || '',
              }
            : null,
          vibes: listing.vibes.map((vibe) => ({
            ...vibe,
            brandId: brandId || '',
          })),
        }),
      );
    }

    if (shouldShowFeaturedListings) {
      return featuredListings ?? [];
    }

    if (shouldShowRegularListings) {
      return (
        listings?.map((listing) => ({
          ...listing,
          isUnlisted: false,
        })) ?? []
      );
    }

    return [];
  };

  const getTitle = (
    hasRecommendations: boolean,
    shouldShowFeaturedListings: boolean,
    shouldShowRegularListings: boolean,
  ) => {
    if (hasRecommendations)
      return `${translate('curatedRecommendations')} • ${
        recommendationDateRangeRef.current
      }`;
    if (shouldShowFeaturedListings || shouldShowRegularListings)
      return translate('moreExperiencesYouMightLike');
    return '';
  };

  const getSubText = (hasRecommendations: boolean) => {
    return hasRecommendations
      ? translate('experiencesAvailableAroundYourBookingDate')
      : undefined;
  };

  const displayListings = getDisplayListings(
    hasRecommendations,
    shouldShowFeaturedListings,
    shouldShowRegularListings,
  );
  const title = getTitle(
    hasRecommendations,
    shouldShowFeaturedListings,
    shouldShowRegularListings,
  );
  const subText = getSubText(hasRecommendations);

  const onViewAllListings = () => {
    customPush({
      pathname: `/e/`,
      search: lang ? `?lang=${lang}` : '',
      unset: ['status'],
    });
    window.sessionStorage.removeItem('confirmation_email');
  };

  // Replaces the page type carried over from checkout, so the confirmation view, the
  // recommendations view and card clicks all report this screen.
  useEffect(() => {
    analyticsManager.setAppData({
      [ANALYTICS_PROPERTY.PageType]: 'Booking confirmation page',
    });
  }, []);

  useEffect(() => {
    analyticsManager.setAppData({
      [ANALYTICS_PROPERTY.CartConfirmationCode]:
        response?.confirmationCode || '',
    });
  }, [response?.confirmationCode]);

  useRecommendationsViewEvent({
    listings: recommendedListings,
    isShown: hasRecommendations,
  });

  useEffect(() => {
    if (!status) {
      customReplace({
        pathname: '/',
      });
    }
  }, []);

  usePageViewEvent({
    eventName: ANALYTICS_EVENT.VIEW_BOOKING_CONFIRMATION,
    payload: {
      [ANALYTICS_PROPERTY.CartConfirmationCode]:
        response?.confirmationCode || '',
    },
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    window.sessionStorage.removeItem('selected_recommended_listing');
  }, []);

  useEffect(() => {
    return () => {
      window.sessionStorage.removeItem('confirmation_email');
      window.sessionStorage.removeItem('recommendation_id');
      window.sessionStorage.removeItem('recommendation_date_range');
    };
  }, []);

  return (
    <div>
      <PageTitle title="experienceCheckoutCompleted" />
      <TopSection>
        <IconWrapper>
          <Check
            color="var(--Text-Tertiary, rgba(0, 0, 0, 0.60))"
            width={42}
            height={42}
          />
        </IconWrapper>
        <Heading>{translate('youreBooked')}</Heading>
        <Text>
          {translate('confirmationEmailSent', { email: emailAddress.current })}
        </Text>
      </TopSection>
      <Divider space={isMobile ? 64 : 96} />
      <BottomSection>
        <TextSection>
          <Title>{title}</Title>
          <Text>{subText}</Text>
        </TextSection>
        <RecommendationCardGrid>
          {isPolling ? (
            <>
              <BookingConfirmationCardSkeleton />
              <BookingConfirmationCardSkeleton />
              <BookingConfirmationCardSkeleton />
            </>
          ) : (
            displayListings?.map((listing, index) => (
              <BookingConfirmationCard
                key={listing.id}
                listing={listing}
                recommendationId={recommendationIdRef.current}
                index={index}
                analyticsListName={
                  hasRecommendations
                    ? RECOMMENDATIONS_LIST_NAME
                    : FEATURED_FALLBACK_LIST_NAME
                }
              />
            ))
          )}
        </RecommendationCardGrid>
        <LinkBackButton onClick={onViewAllListings}>
          {translate('viewAll')}
        </LinkBackButton>
      </BottomSection>
    </div>
  );
};

export default BookingConfirmation;
